import React from 'react';
import styled from 'styled-components';

// 스타일 컴포넌트
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 10px;
  width: 400px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const ModalHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #ededed;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const CloseIcon = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  cursor: pointer;
  font-size: 20px;
  color: #999;
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 10px;
`;

const AmountInfo = styled.div`
  font-size: 12px;
  color: #999;
  margin-bottom: 10px;
`;

const AmountInput = styled.input`
  width: 100%;
  height: 45px;
  border: 1px solid #e1e1e1;
  border-radius: 5px;
  padding: 0 15px;
  font-size: 16px;
  outline: none;
  box-sizing: border-box;
  margin-bottom: 10px;
  
  &:focus {
    border-color: #2282e9;
  }
`;

const AmountButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
`;

const AmountButton = styled.button`
  flex: 1;
  padding: 8px 0;
  border-radius: 5px;
  border: 1px solid #e1e1e1;
  background-color: white;
  color: #333;
  font-size: 14px;
  cursor: pointer;
  
  &:hover {
    border-color: #2282e9;
  }
`;

const SectionDivider = styled.div`
  margin: 20px 0;
`;

const RadioContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
`;

const RadioButton = styled.input`
  margin-right: 8px;
`;

const RadioLabel = styled.label`
  font-size: 14px;
  color: #333;
`;

const TextField = styled.input`
  width: 100%;
  height: 45px;
  border: 1px solid #e1e1e1;
  border-radius: 5px;
  padding: 0 15px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
  margin-bottom: 20px;
  
  &:focus {
    border-color: #2282e9;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0;
`;

const Checkbox = styled.input`
  margin-right: 8px;
`;

const CheckboxLabel = styled.label`
  font-size: 13px;
  color: #555;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const CancelButton = styled.button`
  flex: 1;
  height: 50px;
  border-radius: 50px;
  background-color: #cccccc;
  color: white;
  font-weight: 500;
  border: none;
  cursor: pointer;
  font-size: 16px;
`;

const NextButton = styled.button`
  flex: 1;
  height: 50px;
  border-radius: 50px;
  background-color: #2282e9;
  color: white;
  font-weight: 500;
  border: none;
  cursor: pointer;
  font-size: 16px;
  
  &:disabled {
    background-color: #cccccc;
  }
`;

const ErrorMessage = styled.div`
  color: #ff0000;
  font-size: 14px;
  margin-top: 10px;
  text-align: center;
`;

interface ChargeModalProps {
  onClose: () => void;
  onNext: () => void;
  amount: string;
  paymentMethod: string;
  depositorName: string;
  termsAgreed: boolean;
  onAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAmountSelect: (amount: number) => void;
  onPaymentMethodChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDepositorNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTermsAgreementChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errorMessage?: string;
}

const ChargeModal: React.FC<ChargeModalProps> = ({
  onClose,
  onNext,
  amount,
  paymentMethod,
  depositorName,
  termsAgreed,
  onAmountChange,
  onAmountSelect,
  onPaymentMethodChange,
  onDepositorNameChange,
  onTermsAgreementChange,
  errorMessage
}) => {
  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>플리머니 충전</ModalTitle>
          <CloseIcon onClick={onClose}>✕</CloseIcon>
        </ModalHeader>
        <ModalBody>
          <SectionTitle>충전금액</SectionTitle>
          <AmountInfo>*10,000원 이상부터 충전 가능합니다.</AmountInfo>
          <AmountInput 
            type="text" 
            value={amount}
            onChange={onAmountChange}
            placeholder="0"
          />
          <AmountButtonContainer>
            <AmountButton onClick={() => onAmountSelect(5000)}>+ 5,000</AmountButton>
            <AmountButton onClick={() => onAmountSelect(10000)}>+ 10,000</AmountButton>
            <AmountButton onClick={() => onAmountSelect(100000)}>+ 100,000</AmountButton>
            <AmountButton onClick={() => onAmountSelect(1000000)}>+ 1,000,000</AmountButton>
          </AmountButtonContainer>
          
          <SectionDivider />
          
          <SectionTitle>결제수단</SectionTitle>
          <RadioContainer>
            <RadioButton 
              type="radio" 
              id="bankTransfer" 
              name="paymentMethod" 
              value="무통장 입금"
              checked={paymentMethod === "무통장 입금"}
              onChange={onPaymentMethodChange}
            />
            <RadioLabel htmlFor="bankTransfer">무통장 입금</RadioLabel>
          </RadioContainer>
          
          <SectionDivider />
          
          <SectionTitle>입금자명</SectionTitle>
          <TextField 
            type="text" 
            value={depositorName}
            onChange={onDepositorNameChange}
            placeholder=""
          />
          
          <CheckboxContainer>
            <Checkbox 
              type="checkbox" 
              id="termsAgreement"
              checked={termsAgreed}
              onChange={onTermsAgreementChange}
            />
            <CheckboxLabel htmlFor="termsAgreement">구매 조건 확인 및 결제 진행에 동의</CheckboxLabel>
          </CheckboxContainer>
          
          <ButtonContainer>
            <CancelButton onClick={onClose}>닫기</CancelButton>
            <NextButton 
              onClick={onNext}
              disabled={!amount || parseInt(amount) < 10000 || !depositorName || !termsAgreed || errorMessage !== ""}
            >
              다음
            </NextButton>
          </ButtonContainer>
          {errorMessage && (
            <ErrorMessage>{errorMessage}</ErrorMessage>
          )}
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ChargeModal; 